import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { books as fallbackBooks } from '../data/mock'
import { supabase } from '../lib/supabase'
import { localCoverFor } from '../components/BookCover'
import type { Book } from '../types'
import { useAuth } from './AuthContext'
import { demoKeys, readStored, wordCount } from '../lib/localStore'
import type { BookProject, ProjectChapter } from '../types'

type CatalogContextValue = {
  books: Book[]
  loading: boolean
  bookById: (id: string | undefined) => Book | undefined
}

const CatalogContext = createContext<CatalogContextValue | null>(null)
const legacyDemoIds = new Set(['sapiens','little-prince','atomic','alchemist'])

export function CatalogProvider({ children }: { children: ReactNode }) {
  const { demo } = useAuth()
  const [books, setBooks] = useState<Book[]>(fallbackBooks)
  const [loading, setLoading] = useState(Boolean(supabase))

  useEffect(() => {
    let active = true
    function demoBooks() {
      const projects = readStored<BookProject[]>(demoKeys.projects, []).filter(project => project.status === 'published' && project.visibility === 'public')
      const chapters = readStored<ProjectChapter[]>(demoKeys.chapters, [])
      return [...fallbackBooks, ...projects.map(project => ({
        id: project.id, projectId: project.id, title: project.title, author: project.penName, description: project.description || 'Uma publicação independente no LIVROOM.',
        genres: project.genres, moods: ['Independente'], rating: 0, pages: Math.max(1, Math.ceil(chapters.filter(chapter => chapter.projectId === project.id).reduce((sum, chapter) => sum + wordCount(chapter.content), 0) / 250)),
        accent: project.accent, rightsStatus: 'licensed' as const, creatorId: project.ownerId, clubId: project.clubId || undefined, publicationKind: project.clubId ? 'club' as const : 'individual' as const
      }))]
    }
    if (demo || !supabase) {
      const refresh = () => setBooks(demoBooks())
      refresh(); setLoading(false)
      window.addEventListener('livroom:catalog-refresh', refresh)
      window.addEventListener('livroom:store', refresh)
      return () => { window.removeEventListener('livroom:catalog-refresh', refresh); window.removeEventListener('livroom:store', refresh) }
    }
    const db = supabase
    async function loadCatalog() {
      const [catalogResult, projectsResult] = await Promise.all([
        db.from('catalog_books').select('id,title,author,description,cover_url,genres,page_count,community_rating,accent,rights_status,created_at').order('created_at', { ascending: true }),
        db.from('book_projects').select('id,owner_id,club_id,title,pen_name,description,genres,accent,status,visibility,updated_at').eq('status','published').eq('visibility','public').order('updated_at',{ascending:false})
      ])
      const { data, error } = catalogResult
        if (!active) return
        if (!error && data?.length) {
          const rows = new Map(data.map((row) => [row.id, row]))
          const next = fallbackBooks.map((fallback) => {
            const row = rows.get(fallback.id)
            if (!row) return fallback
            rows.delete(fallback.id)
            return {
              ...fallback,
              title: row.title || fallback.title,
              author: row.author || fallback.author,
              description: row.description || fallback.description,
              coverUrl: localCoverFor(row.id) || row.cover_url || fallback.coverUrl,
              genres: row.genres?.length ? row.genres : fallback.genres,
              rating: Number(row.community_rating || fallback.rating || 0),
              pages: row.page_count || fallback.pages,
              accent: row.accent || fallback.accent,
              rightsStatus: row.rights_status || fallback.rightsStatus
            } satisfies Book
          })
          for (const row of rows.values()) {
            if (legacyDemoIds.has(row.id)) continue
            next.push({
              id: row.id,
              title: row.title,
              author: row.author,
              description: row.description || 'Conheça esta obra no LIVROOM.',
              coverUrl: localCoverFor(row.id) || row.cover_url,
              genres: row.genres || [],
              moods: [],
              rating: Number(row.community_rating || 0),
              pages: row.page_count || 0,
              accent: row.accent || '#6f56ff',
              rightsStatus: row.rights_status
            })
          }
          const projectRows = projectsResult.data || []
          const { data: chapterRows } = projectRows.length ? await db.from('book_project_chapters').select('project_id,word_count').in('project_id', projectRows.map(row => row.id)) : { data: [] }
          for (const project of projectRows) {
            const words = (chapterRows || []).filter(row => row.project_id === project.id).reduce((sum,row) => sum + (row.word_count || 0), 0)
            next.push({
              id: project.id, projectId: project.id, title: project.title, author: project.pen_name, description: project.description || 'Uma publicação independente no LIVROOM.',
              genres: project.genres || [], moods: ['Independente'], rating: 0, pages: Math.max(1, Math.ceil(words/250)), accent: project.accent || '#7557ff',
              rightsStatus: 'licensed', creatorId: project.owner_id, clubId: project.club_id || undefined, publicationKind: project.club_id ? 'club' : 'individual'
            })
          }
          setBooks(next)
        }
        setLoading(false)
    }
    void loadCatalog()
    const refresh = () => { setLoading(true); void loadCatalog() }
    window.addEventListener('livroom:catalog-refresh', refresh)
    return () => { active = false; window.removeEventListener('livroom:catalog-refresh', refresh) }
  }, [demo])

  const value = useMemo<CatalogContextValue>(() => ({
    books,
    loading,
    bookById: (id) => books.find((book) => book.id === id)
  }), [books, loading])

  return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>
}

export function useCatalog() {
  const value = useContext(CatalogContext)
  if (!value) throw new Error('useCatalog must be used inside CatalogProvider')
  return value
}
